import { IconButton, Menu, MenuItem, ThreeDotsIcon } from "@flat-ui";
import { useRef, useState } from "react";
import { MenuItemOption } from "@components/common/mobile/list-item-card-mobile";
import { Box } from "@mui/material";

interface Props<T> {
  /** The item being operated on */
  item: T;
  /** Array of options with label, icon and click handler */
  menuItems: MenuItemOption<T>[];
}

/**
 * Renders a three-dots menu button for mobile list items and handles its open/close state.
 * Expects a list of menu items with label, icon and click handler; renders nothing else.
 *
 * @template T - The type of item being operated on
 */
export function ListItemMenu<T>({ item, menuItems }: Props<T>): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  return (
    <Box
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <IconButton
        ref={buttonRef}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          e.preventDefault();
          setIsOpen(true);
        }}
        size="m"
      >
        <ThreeDotsIcon />
      </IconButton>

      <Menu
        open={isOpen}
        anchorEl={buttonRef.current}
        onClose={() => setIsOpen(false)}
        minWidth="7rem"
      >
        {menuItems.map((menuItem, idx) => (
          <MenuItem
            key={idx}
            label={menuItem.label}
            href={menuItem.getHrefUrl?.(item)}
            onClick={() => {
              menuItem.onSelect(item);
              setIsOpen(false);
            }}
            Icon={menuItem.Icon}
          />
        ))}
      </Menu>
    </Box>
  );
}
